import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { SmartImage, ViewportVideo } from "./media";
import { mediaUrl } from "../../lib/format";

export function StoriesStrip({ stories, onOpen }) {
  if (!stories?.length) return null;
  return (
    <div className="pt-4" data-testid="stories-strip">
      <div className="flex gap-4 overflow-x-auto no-scrollbar px-5 py-4 snap-x">
        {stories.map((s, i) => (
          <button
            key={s.id}
            onClick={() => onOpen(i)}
            data-testid={`story-thumb-${i}`}
            className="snap-start shrink-0 flex flex-col items-center gap-1.5 w-[72px]"
          >
            <div className="story-ring p-[2.5px] rounded-full">
              <div className="rounded-full overflow-hidden bg-zinc-900 w-16 h-16 ring-2 ring-[color:var(--m-bg)]">
                <SmartImage src={s.thumbnail || s.media} priority={i < 4} className="w-full h-full" />
              </div>
            </div>
            <span className="text-[11px] menu-muted truncate w-full text-center font-medium">{s.title}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function StoryViewer({ stories, index, onClose, onCta }) {
  const [cur, setCur] = useState(index);
  const [progress, setProgress] = useState(0);
  const timer = useRef(null);
  const story = stories[cur];
  const isVideo = story?.media_type === "video";
  const DURATION = isVideo ? 12000 : 5000;

  useEffect(() => setCur(index), [index]);

  useEffect(() => {
    setProgress(0);
    const start = Date.now();
    timer.current = setInterval(() => {
      const p = Math.min(1, (Date.now() - start) / DURATION);
      setProgress(p);
      if (p >= 1) next();
    }, 50);
    return () => clearInterval(timer.current);
    // eslint-disable-next-line
  }, [cur]);

  const next = () => {
    clearInterval(timer.current);
    if (cur < stories.length - 1) setCur((c) => c + 1);
    else onClose();
  };
  const prev = () => {
    clearInterval(timer.current);
    if (cur > 0) setCur((c) => c - 1);
  };
  if (!story) return null;

  const ctaLabels = {
    ver_produto: "Ver produto", ver_categoria: "Ver categoria",
    adicionar: "Adicionar ao pedido", whatsapp: "Falar no WhatsApp",
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[80] bg-black flex items-center justify-center"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        data-testid="story-viewer"
      >
        <div className="relative w-full h-full max-w-md mx-auto">
          <div className="absolute top-2 left-2 right-2 z-20 flex gap-1">
            {stories.map((_, i) => (
              <div key={i} className="h-[3px] flex-1 rounded-full bg-white/25 overflow-hidden">
                <div className="h-full bg-white" style={{ width: `${i < cur ? 100 : i === cur ? progress * 100 : 0}%` }} />
              </div>
            ))}
          </div>
          <button onClick={onClose} data-testid="story-close" className="absolute top-6 right-3 z-20 text-white/90 p-1">
            <X size={26} />
          </button>
          <div className="absolute inset-0">
            {isVideo ? (
              <ViewportVideo src={story.media} className="w-full h-full object-cover" />
            ) : (
              <img src={mediaUrl(story.media)} alt={story.title} className="w-full h-full object-cover" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/40" />
          </div>
          {/* tap zones */}
          <button className="absolute left-0 top-0 h-full w-1/3 z-10" onClick={prev} aria-label="anterior" />
          <button className="absolute right-0 top-0 h-full w-1/3 z-10" onClick={next} aria-label="próximo" />
          <button onClick={prev} className="hidden sm:flex absolute left-1 top-1/2 -translate-y-1/2 z-20 text-white/70"><ChevronLeft /></button>
          <button onClick={next} className="hidden sm:flex absolute right-1 top-1/2 -translate-y-1/2 z-20 text-white/70"><ChevronRight /></button>

          <div className="absolute bottom-0 left-0 right-0 z-20 p-5 pb-8">
            <h3 className="font-head text-2xl font-extrabold leading-tight">{story.title}</h3>
            {story.text && <p className="text-sm text-zinc-200 mt-1">{story.text}</p>}
            {story.cta_type && story.cta_type !== "nenhum" && (
              <button
                data-testid="story-cta"
                onClick={() => onCta(story)}
                className="mt-4 w-full rounded-full bg-[#FF6B00] py-3 font-bold text-white orange-glow active:scale-[0.98] transition-transform"
              >
                {ctaLabels[story.cta_type] || "Ver"}
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
